import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import procurementService, {
  PurchaseOrder,
  GoodsReceipt,
  AtRiskTaskResource,
  PurchaseOrderListFilters,
  GoodsReceiptListFilters,
  ProcurementCatalog,
  SupplierSlippageRow,
} from '../../services/api/procurementService';

interface ProcurementState {
  purchaseOrders: PurchaseOrder[];
  selectedPurchaseOrder: PurchaseOrder | null;
  goodsReceipts: GoodsReceipt[];
  selectedGoodsReceipt: GoodsReceipt | null;
  atRisk: AtRiskTaskResource[];
  catalog: ProcurementCatalog | null;
  supplierSlippage: SupplierSlippageRow[];
  loading: {
    purchaseOrders: boolean;
    purchaseOrderDetail: boolean;
    goodsReceipts: boolean;
    goodsReceiptDetail: boolean;
    atRisk: boolean;
    catalog: boolean;
    supplierSlippage: boolean;
  };
  error: string | null;
}

const initialState: ProcurementState = {
  purchaseOrders: [],
  selectedPurchaseOrder: null,
  goodsReceipts: [],
  selectedGoodsReceipt: null,
  atRisk: [],
  catalog: null,
  supplierSlippage: [],
  loading: {
    purchaseOrders: false,
    purchaseOrderDetail: false,
    goodsReceipts: false,
    goodsReceiptDetail: false,
    atRisk: false,
    catalog: false,
    supplierSlippage: false,
  },
  error: null,
};

export const fetchPurchaseOrders = createAsyncThunk(
  'procurement/fetchPurchaseOrders',
  async (filters: PurchaseOrderListFilters | undefined, { rejectWithValue }) => {
    try {
      return await procurementService.listPurchaseOrders(filters ?? {});
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to load purchase orders');
    }
  }
);

export const fetchPurchaseOrder = createAsyncThunk(
  'procurement/fetchPurchaseOrder',
  async (id: string, { rejectWithValue }) => {
    try {
      return await procurementService.getPurchaseOrder(id);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to load purchase order');
    }
  }
);

export const fetchGoodsReceipts = createAsyncThunk(
  'procurement/fetchGoodsReceipts',
  async (filters: GoodsReceiptListFilters | undefined, { rejectWithValue }) => {
    try {
      return await procurementService.listGoodsReceipts(filters ?? {});
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to load goods receipts');
    }
  }
);

export const fetchGoodsReceipt = createAsyncThunk(
  'procurement/fetchGoodsReceipt',
  async (id: string, { rejectWithValue }) => {
    try {
      return await procurementService.getGoodsReceipt(id);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to load goods receipt');
    }
  }
);

export const fetchAtRisk = createAsyncThunk(
  'procurement/fetchAtRisk',
  async (params: { projectId?: string; withinDays?: number } | undefined, { rejectWithValue }) => {
    try {
      return await procurementService.getAtRisk(params ?? {});
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to load at-risk procurement');
    }
  }
);

export const fetchProcurementCatalog = createAsyncThunk(
  'procurement/fetchCatalog',
  async (_, { rejectWithValue }) => {
    try {
      return await procurementService.getCatalog();
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to load procurement catalog');
    }
  }
);

export const fetchSupplierSlippage = createAsyncThunk(
  'procurement/fetchSupplierSlippage',
  async (params: { projectId?: string; days?: number } | undefined, { rejectWithValue }) => {
    try {
      return await procurementService.getSupplierSlippage(params ?? {});
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to load supplier slippage');
    }
  }
);

const procurementSlice = createSlice({
  name: 'procurement',
  initialState,
  reducers: {
    clearSelectedPurchaseOrder(state) {
      state.selectedPurchaseOrder = null;
    },
    clearSelectedGoodsReceipt(state) {
      state.selectedGoodsReceipt = null;
    },
    clearError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPurchaseOrders.pending, (state) => {
        state.loading.purchaseOrders = true;
        state.error = null;
      })
      .addCase(fetchPurchaseOrders.fulfilled, (state, action: PayloadAction<{ rows: PurchaseOrder[] }>) => {
        state.loading.purchaseOrders = false;
        state.purchaseOrders = action.payload.rows;
      })
      .addCase(fetchPurchaseOrders.rejected, (state, action) => {
        state.loading.purchaseOrders = false;
        state.error = action.payload as string;
      })
      .addCase(fetchPurchaseOrder.pending, (state) => {
        state.loading.purchaseOrderDetail = true;
        state.error = null;
      })
      .addCase(fetchPurchaseOrder.fulfilled, (state, action: PayloadAction<PurchaseOrder>) => {
        state.loading.purchaseOrderDetail = false;
        state.selectedPurchaseOrder = action.payload;
      })
      .addCase(fetchPurchaseOrder.rejected, (state, action) => {
        state.loading.purchaseOrderDetail = false;
        state.error = action.payload as string;
      })
      .addCase(fetchGoodsReceipts.pending, (state) => {
        state.loading.goodsReceipts = true;
        state.error = null;
      })
      .addCase(fetchGoodsReceipts.fulfilled, (state, action: PayloadAction<{ rows: GoodsReceipt[] }>) => {
        state.loading.goodsReceipts = false;
        state.goodsReceipts = action.payload.rows;
      })
      .addCase(fetchGoodsReceipts.rejected, (state, action) => {
        state.loading.goodsReceipts = false;
        state.error = action.payload as string;
      })
      .addCase(fetchGoodsReceipt.pending, (state) => {
        state.loading.goodsReceiptDetail = true;
        state.error = null;
      })
      .addCase(fetchGoodsReceipt.fulfilled, (state, action: PayloadAction<GoodsReceipt>) => {
        state.loading.goodsReceiptDetail = false;
        state.selectedGoodsReceipt = action.payload;
      })
      .addCase(fetchGoodsReceipt.rejected, (state, action) => {
        state.loading.goodsReceiptDetail = false;
        state.error = action.payload as string;
      })
      .addCase(fetchAtRisk.pending, (state) => {
        state.loading.atRisk = true;
        state.error = null;
      })
      .addCase(fetchAtRisk.fulfilled, (state, action: PayloadAction<AtRiskTaskResource[]>) => {
        state.loading.atRisk = false;
        state.atRisk = action.payload;
      })
      .addCase(fetchAtRisk.rejected, (state, action) => {
        state.loading.atRisk = false;
        state.error = action.payload as string;
      })
      .addCase(fetchProcurementCatalog.pending, (state) => {
        state.loading.catalog = true;
        state.error = null;
      })
      .addCase(fetchProcurementCatalog.fulfilled, (state, action: PayloadAction<ProcurementCatalog>) => {
        state.loading.catalog = false;
        state.catalog = action.payload;
      })
      .addCase(fetchProcurementCatalog.rejected, (state, action) => {
        state.loading.catalog = false;
        state.error = action.payload as string;
      })
      .addCase(fetchSupplierSlippage.pending, (state) => {
        state.loading.supplierSlippage = true;
        state.error = null;
      })
      .addCase(fetchSupplierSlippage.fulfilled, (state, action: PayloadAction<SupplierSlippageRow[]>) => {
        state.loading.supplierSlippage = false;
        state.supplierSlippage = action.payload;
      })
      .addCase(fetchSupplierSlippage.rejected, (state, action) => {
        state.loading.supplierSlippage = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearSelectedPurchaseOrder, clearSelectedGoodsReceipt, clearError } = procurementSlice.actions;
export default procurementSlice.reducer;
